import React, { useState } from 'react'
import AuthNavBar from '../Dashboard/AuthNavBar'
import SidePanel from '../SidePanel/SidePanel'
import useGlobalTheme from '../../hooks/useGlobalTheme'
import {
	SparklesIcon,
	FunnelIcon,
	ShieldCheckIcon,
	MapIcon,
	EnvelopeIcon,
	BellAlertIcon,
	ChevronDownIcon,
	ChevronRightIcon,
} from '@heroicons/react/24/outline'
function Tutorial() {
	const theme = useGlobalTheme()
	const isLight = theme === 'light'
	const [isPanelOpen, setIsPanelOpen] = useState(false)
	const [activeMainTab, setActiveMainTab] = useState('getting-started')
	const [activeSubTab, setActiveSubTab] = useState('connect-email')
	const [expandedSections, setExpandedSections] = useState(['getting-started'])
	const [imageLoading, setImageLoading] = useState(true)

	const toggleSection = sectionId => {
		setExpandedSections(prev => {
			if (prev.includes(sectionId)) {
				return []
			} else {
				setActiveMainTab(sectionId)
				const section = onboardingSections.find(s => s.id === sectionId)
				if (section && section.subTabs.length > 0) {
					setActiveSubTab(section.subTabs[0].id)
					setImageLoading(true) // Reset loading state when changing sections
				}
				return [sectionId]
			}
		})
	}

	const handleImageLoad = () => {
		setImageLoading(false)
	}

	const handleSubTabClick = (sectionId, subTabId) => {
		setActiveMainTab(sectionId)
		setActiveSubTab(subTabId)
		setImageLoading(true)
	}

	const onboardingSections = [
		{
			id: 'getting-started',
			title: 'Getting Started',
			icon: SparklesIcon,
			color: 'from-blue-600 to-cyan-600',
			subTabs: [
				{
					id: 'connect-email',
					title: 'Connect Email',
					description: 'Select the account LoadLine will send from.',
					tutorialFile:
						'https://loadline-tutorial.nyc3.cdn.digitaloceanspaces.com/connect-email-new.mp4',
				},
				{
					id: 'default-template',
					title: 'Create New Template',
					description: 'Set the starting message for this tab.',
					tutorialFile:
						'https://loadline-tutorial.nyc3.cdn.digitaloceanspaces.com/create-template.mp4',
				},
			],
		},
		{
			id: 'filters',
			title: 'Filters',
			icon: FunnelIcon,
			color: 'from-emerald-600 to-green-600',
			subTabs: [
				{
					id: 'tab-filters',
					title: 'Tab Filters',
					description: 'Distance, rate, state, equipment.',
					tutorialFile:
						'https://loadline-tutorial.nyc3.cdn.digitaloceanspaces.com/tab-filter.mp4',
				},
				{
					id: 'global-filters',
					title: 'Global Filters',
					description: 'Defaults applied to every new tab.',
					tutorialFile:
						'https://loadline-tutorial.nyc3.cdn.digitaloceanspaces.com/global-filter.mp4',
				},
			],
		},
		{
			id: 'smart-alerts',
			title: 'Smart Alerts',
			icon: BellAlertIcon,
			color: 'from-purple-600 to-pink-600',
			subTabs: [
				{
					id: 'enable-alerts',
					title: 'Enable Match Alerts',
					description: 'Turn on real-time pop-ups.',
					tutorialFile:
						'https://loadline-tutorial.nyc3.cdn.digitaloceanspaces.com/smart-alerts.mp4',
				},
				{
					id: 'pin-clear-alerts',
					title: 'Pin / Clear Alerts',
					description: 'Keep important ones, dismiss the rest.',
					tutorialFile:
						'https://loadline-tutorial.nyc3.cdn.digitaloceanspaces.com/pin-alerts.mp4',
				},
			],
		},
		{
			id: 'email-automation',
			title: 'Email Automation',
			icon: EnvelopeIcon,
			color: 'from-indigo-600 to-blue-600',
			subTabs: [
				{
					id: 'one-click-send',
					title: 'One-Click Send',
					description: 'Fire the template + tracking pixel.',
					tutorialFile:
						'https://loadline-tutorial.nyc3.cdn.digitaloceanspaces.com/one-click-email.mp4',
				},
				{
					id: 'reply-in-place',
					title: 'Reply In-Place',
					description: 'Answer broker emails without leaving the board.',
					tutorialFile:
						'https://loadline-tutorial.nyc3.cdn.digitaloceanspaces.com/reply-in-place.mp4',
				},
			],
		},
		{
			id: 'broker-intelligence',
			title: 'Broker Intelligence',
			icon: ShieldCheckIcon,
			color: 'from-orange-600 to-red-600',
			subTabs: [
				{
					id: 'risk-assessment',
					title: 'Risk Assessment',
					description: 'Instant red / yellow / green score.',
					tutorialFile:
						'https://loadline-tutorial.nyc3.cdn.digitaloceanspaces.com/quick-risk.mp4',
				},
				{
					id: 'factoring-score',
					title: 'Factoring Score',
					description: 'Payment reliability at a glance.',
					tutorialFile:
						'https://loadline-tutorial.nyc3.cdn.digitaloceanspaces.com/risk-broker.mp4',
				},
			],
		},
		{
			id: 'integrated-maps',
			title: 'Integrated Maps',
			icon: MapIcon,
			color: 'from-teal-600 to-cyan-600',
			subTabs: [
				{
					id: 'exact-miles',
					title: 'Built-in Google Maps',
					description: 'Open a Google Maps overlay for any load.',
					tutorialFile:
						'https://loadline-tutorial.nyc3.cdn.digitaloceanspaces.com/google-maps.mp4',
				},
			],
		},
	]

	const getCurrentSubTab = () => {
		const currentSection = onboardingSections.find(
			section => section.id === activeMainTab
		)
		return currentSection?.subTabs.find(subTab => subTab.id === activeSubTab)
	}

	const currentSubTab = getCurrentSubTab()

	return (
		<>
			<AuthNavBar />
			<SidePanel
				isOpen={isPanelOpen}
				onOpen={() => setIsPanelOpen(true)}
				onClose={() => setIsPanelOpen(false)}
			/>
			<div className={`min-h-screen ${isLight ? 'bg-white' : 'bg-[#15202b]'}`}>
				<div className="max-w-7xl mx-auto w-full pt-[5vh] pb-8 sm:px-6 lg:px-8">
					<div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6">
						{/* Left Section - Navigation */}
						<div className={`p-6 rounded-lg ${
							isLight
								? 'bg-white ring-1 ring-gray-200 shadow-lg'
								: 'bg-[#15202b] ring-1 ring-white/10 shadow-[0_0_15px_rgba(59,130,246,0.1)]'
						}`}>
							<div>
								<div className="space-y-4">
									{onboardingSections.map((section, sectionIndex) => {
										const Icon = section.icon
										const isExpanded = expandedSections.includes(section.id)

										return (
											<div key={section.id} className="space-y-3">
												{/* Main Section Header */}
												<button
													onClick={() => toggleSection(section.id)}
													className={`w-full text-left p-3 rounded-lg transition-all duration-200 ${
														isLight
															? 'bg-gray-50 hover:bg-gray-100'
															: 'bg-[#263340] hover:bg-[#2c3b4a]'
													}`}
												>
													<div className="flex items-center gap-3">
														<div className="flex items-center gap-2">
															<div
																className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center bg-gradient-to-br ${section.color}`}
															>
																<Icon className="w-5 h-5 text-white" />
															</div>
														</div>
														<div className="flex-1">
															<h3 className={`text-sm font-semibold ${isLight ? 'text-gray-900' : 'text-white'}`}>
																{section.title}
															</h3>
														</div>
														<div className="flex items-center gap-2">
															{isExpanded ? (
																<ChevronDownIcon className={`w-4 h-4 ${isLight ? 'text-gray-500' : 'text-[#64748b]'}`} />
															) : (
																<ChevronRightIcon className={`w-4 h-4 ${isLight ? 'text-gray-500' : 'text-[#64748b]'}`} />
															)}
														</div>
													</div>
												</button>

												{/* Sub-tabs */}
												{isExpanded && (
													<div className="ml-6 space-y-1">
														{section.subTabs.map(subTab => {
															const isActive = activeSubTab === subTab.id

															return (
																<button
																	key={subTab.id}
																	onClick={() =>
																		handleSubTabClick(section.id, subTab.id)
																	}
																	className={`w-full text-left p-1.5 rounded-lg transition-all duration-200 ${
																		isActive
																			? 'bg-blue-600 hover:bg-blue-700'
																			: isLight
																				? 'hover:bg-gray-100'
																				: 'hover:bg-[#263340]/50'
																	}`}
																>
																	<div className="flex items-center gap-2">
																		<div
																			className={`w-2 h-2 rounded-full ${
																				isActive
																					? 'bg-white'
																					: isLight ? 'bg-gray-400' : 'bg-[#64748b]'
																			}`}
																		/>
																		<span
																			className={`text-xs ${
																				isActive
																					? 'text-white font-medium'
																					: isLight ? 'text-gray-700' : 'text-[#e2e8f0]'
																			}`}
																		>
																			{subTab.title}
																		</span>
																	</div>
																</button>
															)
														})}
													</div>
												)}
											</div>
										)
									})}
								</div>
							</div>
						</div>

						{/* Right Section - Content Area */}
						<div className={`p-6 rounded-lg ${
							isLight
								? 'bg-white ring-1 ring-gray-200 shadow-lg'
								: 'bg-[#15202b] ring-1 ring-white/10 shadow-[0_0_15px_rgba(59,130,246,0.1)]'
						}`}>
							<div className="h-full flex flex-col">
								{/* Content Header */}
								<div className="mb-6">
									<h2 className={`text-xl font-semibold mb-2 ${isLight ? 'text-gray-900' : 'text-white'}`}>
										{currentSubTab?.title || 'Welcome to LoadLine.AI'}
									</h2>
									<p className={`text-sm ${isLight ? 'text-gray-500' : 'text-[#94a3b8]'}`}>
										{currentSubTab?.description ||
											"Let's get you started with LoadLine.AI"}
									</p>
								</div>

								{/* GIF/Image Preview */}
								<div className="flex-1">
									<div className={`rounded-lg overflow-hidden w-full h-[500px] relative ${
										isLight
											? 'bg-gray-50 border border-gray-200 ring-1 ring-gray-100'
											: 'bg-[#0a1929] border border-[#1e3a5f] ring-1 ring-white/5'
									}`}>
										{imageLoading && (
											<div className={`absolute inset-0 flex items-center justify-center z-10 ${
												isLight ? 'bg-gray-50' : 'bg-[#0a1929]'
											}`}>
												<div className="flex flex-col items-center gap-3">
													<div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
													<p className={`text-sm ${isLight ? 'text-gray-500' : 'text-[#94a3b8]'}`}>
														Loading demo...
													</p>
												</div>
											</div>
										)}
										{currentSubTab?.tutorialFile?.endsWith('.mp4') ? (
											<video
												key={currentSubTab?.id || 'default'}
												src={currentSubTab?.tutorialFile}
												className="w-full h-full object-contain"
												autoPlay
												loop
												muted
												playsInline
												onLoadedData={handleImageLoad}
												onError={() => setImageLoading(false)}
											/>
										) : (
											<img
												key={currentSubTab?.id || 'default'}
												src={
													currentSubTab?.tutorialFile ||
													'https://loadline-tutorial.nyc3.cdn.digitaloceanspaces.com/gify.gif'
												}
												alt={`${currentSubTab?.title || 'LoadLine.AI'} demo`}
												width={600}
												height={340}
												className="w-full h-full object-contain"
												onLoad={handleImageLoad}
												onError={() => setImageLoading(false)}
											/>
										)}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	)
}

export default Tutorial
